import { createSignal, untrack, type JSX, type VoidComponent, createMemo } from "solid-js";
import type { ExtendableInputProps } from "./Input";
import Input from "./Input";
import { isEmpty } from "lodash-es";

type DateProps = {
  id: string;
  ariaLabel: string;
  value: string;
  errorMessage?: string;
  type: "date" | "datetime-local" | "time";
  onBlur: JSX.FocusEventHandler<HTMLInputElement, FocusEvent>;
  onInput?: JSX.InputEventHandler<HTMLInputElement, InputEvent>;
  disabled?: boolean;
};
type DateInputProps = DateProps & ExtendableInputProps;

const BaseDateInput: VoidComponent<DateProps> = (props) => {
  const hasError = () => props.errorMessage !== undefined;
  const [currentValue, setCurrentValue] = createSignal(untrack(() => props.value));
  const isDisabled = createMemo(() => props.disabled === true);
  const classes = createMemo(() => {
    if (hasError()) {
      return "bg-surface-destructive border-error";
    } else if (isDisabled()) {
      return "bg-surface-disabled border-disabled";
    } else {
      return "bg-surface-default border-default";
    }
  });

  return (
    <input
      id={props.id}
      aria-label={props.ariaLabel}
      type={props.type}
      class="h-9.5 w-full rounded-sm border p-1.5 text-base-medium focus-visible:outline-default"
      classList={{
        [classes()]: true,
        "date-time-placeholder text-placeholder": isEmpty(currentValue()),
        "text-primary": !isEmpty(currentValue()),
      }}
      disabled={isDisabled()}
      value={props.value}
      onInput={(e) => {
        props.onInput?.(e);
        setCurrentValue(e.target.value);
      }}
      onBlur={(e) => {
        props.onBlur(e);
      }}
    />
  );
};

export const DateInput: VoidComponent<Omit<DateInputProps, "id" | "type">> = (props) => (
  <Input {...props}>{(id) => <BaseDateInput {...props} id={id} type="date" />}</Input>
);

export const CompactDateInput: VoidComponent<Omit<DateProps, "type">> = (props) => (
  <BaseDateInput {...props} type="date" />
);

export const TimeInput: VoidComponent<Omit<DateInputProps, "id" | "type">> = (props) => (
  <Input {...props}>{(id) => <BaseDateInput {...props} id={id} type="time" />}</Input>
);

export const CompactTimeInput: VoidComponent<Omit<DateProps, "type">> = (props) => (
  <BaseDateInput {...props} type="time" />
);

export const DateTimeInput: VoidComponent<Omit<DateInputProps, "id" | "type">> = (props) => (
  <Input {...props}>{(id) => <BaseDateInput {...props} id={id} type="datetime-local" />}</Input>
);

export const CompactDateTimeInput: VoidComponent<Omit<DateProps, "type">> = (props) => (
  <BaseDateInput {...props} type="datetime-local" />
);
